"use client";

import type { ReactNode } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, Sparkles, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { registroHref, type PricingPlan } from "@/lib/pricing/plan-slug";
import { PlanCtaLink } from "./plan-cta-link";

export type { PricingPlan };

export type PricingBonus = {
  title: string;
  value: string;
};

type PricingCardsProps = {
  monthlyPriceCents: number;
  annualPriceCents: number;
  /** Free trial days, applied to both plans (matches the Stripe plan config). */
  trialDays?: number;
  features?: string[];
  /** Rendered inside both cards, below the features. Used by VSL offers. */
  bonuses?: PricingBonus[];
  bonusesTotalValue?: string;
  /** Rendered under both CTAs (e.g. "No pagas nada hoy · Garantía de 30 días"). */
  ctaFooter?: ReactNode;
  /** When provided, CTAs are buttons calling this. Otherwise they link out. */
  onSelect?: (plan: PricingPlan) => void;
  loadingPlan?: PricingPlan | null;
  /** Builds the CTA link target when no `onSelect` is given. */
  hrefForPlan?: (plan: PricingPlan) => string;
  className?: string;
};

export const DEFAULT_PRICING_FEATURES = [
  "Acceso completo a todos los programas de entrenamiento",
  "Recetas saludables y planes de alimentación",
  "Cursos de salud y bienestar holístico",
  "Seguimiento de progreso personalizado",
  "Acceso a sesiones de Zoom con el coach",
  "Actualizaciones y nuevo contenido mensual",
];

export function PricingCards({
  monthlyPriceCents,
  annualPriceCents,
  trialDays = 0,
  features = DEFAULT_PRICING_FEATURES,
  bonuses,
  bonusesTotalValue,
  ctaFooter,
  onSelect,
  loadingPlan = null,
  hrefForPlan = registroHref,
  className,
}: PricingCardsProps) {
  const monthlyPrice = monthlyPriceCents / 100;
  const annualPrice = annualPriceCents / 100;
  const annualEquivalentMonthly = annualPrice / 12;
  const annualDailyCost = annualPrice / 365;

  // Real savings: annual vs paying monthly for a year
  const yearlyIfPaidMonthly = monthlyPrice * 12;
  const annualSavingsAmount = yearlyIfPaidMonthly - annualPrice;
  const annualSavingsPercent = Math.round(
    (annualSavingsAmount / yearlyIfPaidMonthly) * 100,
  );

  const ctaLabel = (plan: PricingPlan) =>
    trialDays > 0
      ? `Comenzar ${trialDays} Días Gratis`
      : plan === "annual"
        ? "Comenzar Plan Anual"
        : "Comenzar Plan Mensual";

  const renderCta = (plan: PricingPlan) => {
    const isAnnual = plan === "annual";
    const variant = isAnnual ? "default" : "outline";
    const ctaClassName = cn(
      "w-full",
      isAnnual &&
        "bg-brand-blue text-base font-semibold text-white shadow-lg shadow-brand-blue/40 hover:bg-brand-blue/90",
    );
    const size = isAnnual ? "xl" : "lg";
    if (!onSelect) {
      return (
        <Button asChild className={ctaClassName} size={size} variant={variant}>
          <PlanCtaLink plan={plan} href={hrefForPlan(plan)}>
            {ctaLabel(plan)}
          </PlanCtaLink>
        </Button>
      );
    }
    return (
      <Button
        className={ctaClassName}
        size={size}
        variant={variant}
        onClick={() => onSelect(plan)}
        disabled={loadingPlan !== null}
      >
        {loadingPlan === plan ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Procesando...
          </>
        ) : (
          ctaLabel(plan)
        )}
      </Button>
    );
  };

  const featureList = (
    <ul className="flex-1 space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <Check className="text-brand-red mr-3 mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="text-muted-foreground text-sm">{feature}</span>
        </li>
      ))}
    </ul>
  );

  const bonusList = bonuses && bonuses.length > 0 && (
    <div className="border-brand-red/20 bg-brand-red/5 mt-5 rounded-lg border p-4">
      <div className="mb-2 flex items-center gap-2">
        <Gift className="text-brand-red h-4 w-4" />
        <span className="text-sm font-semibold">
          Bonos incluidos hoy
          {bonusesTotalValue ? ` (${bonusesTotalValue} de valor)` : ""}
        </span>
      </div>
      <ul className="space-y-1.5">
        {bonuses.map((bonus) => (
          <li
            key={bonus.title}
            className="flex items-start justify-between gap-2"
          >
            <span className="text-muted-foreground text-xs">{bonus.title}</span>
            <span className="text-brand-red shrink-0 text-xs font-semibold">
              {bonus.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const footer = ctaFooter && (
    <div className="text-muted-foreground mt-3 text-center text-xs">
      {ctaFooter}
    </div>
  );

  return (
    <div className={cn("grid gap-6 md:grid-cols-2 md:gap-8", className)}>
      {/* Monthly Plan - Anchor price */}
      <div className="border-border bg-card hover:border-muted-foreground/30 overflow-hidden rounded-xl border transition-[border-color,box-shadow] duration-300 ease-out">
        <Card className="flex h-full flex-col border-0 bg-transparent">
          <CardContent className="flex flex-1 flex-col p-6 pt-6">
            <div className="mb-1">
              <h2 className="title text-xl font-semibold">Plan Mensual</h2>
              <p className="text-muted-foreground text-sm">
                Suscripción flexible mes a mes
              </p>
            </div>

            <div className="my-5">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold tracking-tight md:text-5xl">
                  ${monthlyPrice.toFixed(2)}
                </span>
                <span className="text-muted-foreground text-base">/mes</span>
              </div>
              <p className="text-muted-foreground mt-2 text-sm">
                ${yearlyIfPaidMonthly.toFixed(2)}/año si pagas mensualmente
              </p>
              {trialDays > 0 && (
                <Badge className="mt-3 border-blue-500/30 bg-blue-500/20 text-blue-400">
                  <Gift className="mr-1 h-3 w-3" />
                  {trialDays} días gratis
                </Badge>
              )}
            </div>

            {featureList}
            {bonusList}
          </CardContent>
          <CardFooter className="flex-col p-6 pt-0">
            {renderCta("monthly")}
            {footer}
          </CardFooter>
        </Card>
      </div>

      {/* Annual Plan - Recommended */}
      <div className="animate-rotate-border from-brand-blue/60 via-brand-red/60 to-brand-blue/60 shadow-brand-blue/10 bg-conic/[from_var(--border-angle)] relative overflow-hidden rounded-xl from-0% via-50% to-100% p-px shadow-lg">
        <div className="absolute -top-px left-1/2 z-10 -translate-x-1/2">
          <div className="from-brand-blue to-brand-red flex items-center gap-1.5 rounded-b-lg bg-gradient-to-r px-4 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-white" />
            <span className="text-xs font-semibold text-white">
              {annualSavingsPercent}% de descuento
            </span>
          </div>
        </div>

        <Card className="bg-card flex h-full flex-col rounded-xl border-0">
          <CardContent className="flex flex-1 flex-col p-6 pt-10">
            <div className="mb-1">
              <h2 className="title text-xl font-semibold">Plan Anual</h2>
              <p className="text-muted-foreground text-sm">
                Mejor valor - paga una vez al año
              </p>
            </div>

            <div className="my-5">
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground text-xl line-through">
                  ${yearlyIfPaidMonthly.toFixed(2)}
                </span>
                <span className="text-4xl font-bold tracking-tight md:text-5xl">
                  ${annualPrice.toFixed(2)}
                </span>
                <span className="text-muted-foreground text-base">/año</span>
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-foreground font-medium">
                  Solo ${annualEquivalentMonthly.toFixed(2)}/mes{" "}
                  <span className="text-muted-foreground text-sm font-normal">
                    (${annualDailyCost.toFixed(2)} al día)
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="border-green-500/30 bg-green-500/20 text-green-400">
                    Ahorras {annualSavingsPercent}% ($
                    {annualSavingsAmount.toFixed(0)})
                  </Badge>
                  {trialDays > 0 && (
                    <Badge className="border-blue-500/30 bg-blue-500/20 text-blue-400">
                      <Gift className="mr-1 h-3 w-3" />
                      {trialDays} días gratis
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {featureList}
            {bonusList}
          </CardContent>
          <CardFooter className="flex-col p-6 pt-0">
            {renderCta("annual")}
            {footer}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
